import { defineStore } from 'pinia';
import { useDepartmentStore } from '@/stores/department';
import constants from '@/utils/constants';
import { addMonths } from 'date-fns';
import { format } from '@/utils/date-fns';
import { useProfilesStore } from '@/stores/profiles';
import api from '@/api';
import { MeetingSubscription, ScreenProfile } from '@/graphql/generated-types';
import { MeetingEntry, MeetingFragment } from '@/graphql/custom-types';

const DEFAULT_MEETING: MeetingEntry = {
  __typename: 'SingleMeeting',
  agendaPoints: [],
  cancelled: false,
  departments: [],
  category: '',
  coverImage: { id: '', blurhash: '', source: '' },
  endDate: '',
  id: '',
  institution: { color: '', name: '', logo: { source: '' } },
  location: '',
  participants: [],
  startDate: '',
  title: '',
  participating: false,
  signupPossible: true,
  suggestions: [],
};

export interface IMeetingState {
  all: MeetingFragment[];
  selected: MeetingEntry | null;
}

export const useMeetingsStore = defineStore('meetings', {
  state: (): IMeetingState => ({
    all: [],
    selected: DEFAULT_MEETING,
  }),
  actions: {
    async getMeetings() {
      const departmentStore = useDepartmentStore();

      if (!departmentStore.id) {
        throw new Error('No department id provided');
      }

      const today = new Date();

      await api.meetings
        .getMeetingRange(
          { departments: [departmentStore.id] },
          format(new Date(), 'yyyy-MM-dd'),
          format(addMonths(today, 12), 'yyyy-MM-dd'),
          constants.timezone
        )
        .then((meetingRange) => {
          this.all = meetingRange;
        })
        .catch((error) => {
          console.log('Error getting meetings', error);
        });
    },
    async getSelectedMeeting(meetingId: string) {
      await api.meetings
        .getMeetingEntry(meetingId)
        .then((meetingEntry) => {
          const updatedMeetingEntry = {
            ...meetingEntry,
            participants: (meetingEntry.participants || []).map(
              (participant) => {
                if (!participant.showImage)
                  participant.displayImage = constants.avatarPlaceholder;
                return participant;
              }
            ),
            suggestions: (meetingEntry.suggestions || []).map((suggestion) => {
              if (suggestion.suggester && !suggestion.suggester.showImage) {
                suggestion.suggester.displayImage = constants.avatarPlaceholder;
              }

              return suggestion;
            }),
          };

          this.selected = updatedMeetingEntry as MeetingEntry;
        })
        .catch((error) => {
          console.log('Could not get meeting entry: ', error);
        });
    },
    async meetingSignupScreen(
      subscription: MeetingSubscription,
      profile: ScreenProfile,
      participating: boolean
    ) {
      const profilesStore = useProfilesStore();

      const mutation = participating
        ? api.meetings.signup
        : api.meetings.cancelSignup;

      await mutation(subscription, profile)
        .then(async () => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: true,
          });

          await this.getSelectedMeeting(subscription.id);
        })
        .catch((error) => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: false,
          });
          throw new Error('Error signing up to meeting', error);
        });
    },
    async meetingSuggest({
      profile,
      meetingId,
      suggestion,
    }: {
      profile: ScreenProfile;
      meetingId: string;
      suggestion: string;
    }) {
      await api.meetings
        .addSuggestion({ profile, meetingId, suggestion })
        .then(() => {
          this.getSelectedMeeting(meetingId);
        })
        .catch((error) => {
          throw new Error('Error saving suggestion', error);
        });
    },
  },
});
